import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import {
	IAppendAliasListProps,
	IAddQuestionProps,
	IAnswer,
	IDeleteQuestionsListProps,
	IEditQuestionProps,
	IGetQuestionsListProps,
	IQuestionItem,
	IResponseAddingQuestion,
	IResponseQuestion,
	IResponseQuestionsList,
	IApproveCandidateListProps,
	IModifyAnswersProps,
	IReplaceTextProps,
} from "@/types/qas/qasQuestionTypes";
import { IResponseReplaceText } from "@/types/qas/qasDocumentTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";
import { IQasQuestionState } from "@/types/qas/qasQuestionTypes";

const initialState: IQasQuestionState = {
	questionsList: {
		status: RequestStatus.IDLE,
		data: [],
		error: ResponseStatus.SUCCESS,
		message: "",
		update: false,
	},
	question: {
		status: RequestStatus.IDLE,
		aliases: [],
		candidates: [],
		answers: [],
		channelsIdAndCountAnswers: {},
		questionId: null,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	addingQuestion: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
	},
	editingQuestion: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		autosave: false,
	},
	deletingQuestion: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		deletingAnswers: false,
	},
	replaceInQuestion: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		replacements: null,
	},
	editingAnswers: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	// манипуляции с вопросами
	questionManipulation: {
		questionsList: {
			status: RequestStatus.IDLE,
			data: [],
			error: ResponseStatus.SUCCESS,
			message: "",
		},
		appendAliasList: {
			status: RequestStatus.IDLE,
			error: ResponseStatus.SUCCESS,
			message: "",
		},
		question: {
			status: RequestStatus.IDLE,
			aliases: [],
			candidates: [],
			answers: [],
			error: ResponseStatus.SUCCESS,
			message: "",
		},
		editingQuestion: {
			status: RequestStatus.IDLE,
			error: ResponseStatus.SUCCESS,
			message: "",
		},
		approveCandidateList: {
			status: RequestStatus.IDLE,
			error: ResponseStatus.SUCCESS,
			message: "",
			deleteOnly: false,
		},
	},
};

// получение списка вопросов
export const getQuestionsList = createAsyncThunk(
	"qas/getQuestionsList",
	async ({ question, answer, category, onlyNew, created, modified, createdBefore }: IGetQuestionsListProps, { rejectWithValue, signal }) => {
		try {
			const response: AxiosResponse<IResponseQuestionsList | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_LIST}`, {
				signal,
				params: {
					category,
					question,
					answer,
					onlyNew,
					created,
					modified,
					createdBefore,
				},
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// получение списка ответов на вопрос
export const getQuestion = createAsyncThunk("qas/getQuestion", async function (questionId: string, { rejectWithValue, signal }) {
	try {
		const response: AxiosResponse<IResponseQuestion | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_GET}/${questionId}`, {
			signal,
		});

		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление вопроса с ответами
export const addQuestion = createAsyncThunk("qas/addQuestion", async ({ question, categoryId }: IAddQuestionProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseAddingQuestion> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_ADD}`, {
			question,
			category: categoryId,
			answers: JSON.stringify([]),
			aliases: JSON.stringify([]),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение вопроса
export const editQuestion = createAsyncThunk(
	"qas/editQuestion",
	async ({ questionId, answers, question, aliases, candidates, categoryId, incomplete, autosave }: IEditQuestionProps, { rejectWithValue, dispatch }) => {
		try {
			autosave && dispatch(addFlagAutosaveQuestion()); // ставим флаг автосохранения
			const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_EDIT}/${questionId}`, {
				data: JSON.stringify({ answers, question, aliases, candidates, category: categoryId, incomplete }),
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// удаление вопроса
export const deleteQuestion = createAsyncThunk("qas/deleteQuestion", async (questionId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_DELETE}/${questionId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// поиск и замена текста во всех ответах
export const replaceTextInAllAnswers = createAsyncThunk(
	"qas/replaceTextInAllAnswers",
	async ({ categoryId, mode, search, channelFrom, text, channelTo }: IReplaceTextProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseReplaceText> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_REPLACE}`, {
				category: categoryId,
				mode,
				search,
				channelfrom: channelFrom,
				text,
				channelto: channelTo,
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// удаление списка вопросов
export const deleteQuestionsList = createAsyncThunk("qas/deleteQuestionsList", async ({ questionsIdList, type }: IDeleteQuestionsListProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_LIST_DELETE}`, {
			questions: JSON.stringify(questionsIdList),
			type,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// замена ответов
export const modifyAnswers = createAsyncThunk("qas/modifyAnswers", async ({ answers }: IModifyAnswersProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseReplaceText> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_ANSWERS_EDIT}`, {
			answers: JSON.stringify(answers),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// получение полного списка вопросов для перемещения
export const getQuestionsListForMoving = createAsyncThunk("qas/getQuestionsListForMoving", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseQuestionsList | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_LIST}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// получение списка ответов на вопрос для перемещения
export const getQuestionForMoving = createAsyncThunk("qas/getQuestionForMoving", async (questionId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseQuestion | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_GET}/${questionId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение вопроса для перемещения
export const editQuestionForMoving = createAsyncThunk(
	"qas/editQuestionForMoving",
	async ({ questionId, answers, question, aliases, candidates, categoryId }: IEditQuestionProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_EDIT}/${questionId}`, {
				data: JSON.stringify({ answers, question, aliases, candidates, category: categoryId }),
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// добавление списка алиасов/кандидатов к другому вопросу
export const appendAliasList = createAsyncThunk("qas/appendAliasList", async ({ questionId, aliasList, candidateList }: IAppendAliasListProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_APPEND}/${questionId}`, {
			aliases: JSON.stringify(aliasList),
			candidates: JSON.stringify(candidateList),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// перемещение списка кандидатов в формулировки/удаление списка кандидатов (если destinationQuestionId = -1)
export const approveCandidateList = createAsyncThunk(
	"qas/approveCandidateList",
	async ({ destinationQuestionId, candidateList }: IApproveCandidateListProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_APPROVE}/${destinationQuestionId}`, {
				candidates: JSON.stringify(candidateList),
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

const qasQuestionSlice = createSlice({
	name: "qasQuestion",
	initialState,
	reducers: {
		// добавление id активного вопроса
		addActiveQuestionId: (state, action: PayloadAction<string>) => {
			state.question.questionId = action.payload;
		},
		// изменение одного из вариантов вопросов
		editingAnswerAlias: (state, action: PayloadAction<{ idx: number; alias: string; listType: "aliases" | "candidates" }>) => {
			state.question[action.payload.listType][action.payload.idx] = action.payload.alias;
		},
		// добавление варианта вопроса
		addAlias: (state) => {
			state.question.aliases.push("");
		},
		// удаление варианта вопроса
		deleteAlias: (state, action: PayloadAction<{ idx: number; listType: "aliases" | "candidates" }>) => {
			state.question[action.payload.listType].splice(action.payload.idx, 1);
		},
		// удаление нескольких вариантов алиасов/кандидатов
		deleteAliases: (state, action: PayloadAction<{ aliasIdxList: number[]; listType: "aliases" | "candidates" }>) => {
			state.question[action.payload.listType] = state.question[action.payload.listType].filter((_alias, idx) => !action.payload.aliasIdxList.includes(idx));
		},
		// перемещение кандидатов в алиасы
		moveCandidatesToAliases: (state, action: PayloadAction<number[]>) => {
			const selectedCandidates = state.question.candidates.filter((_alias, idx) => action.payload.includes(idx));
			state.question.candidates = state.question.candidates.filter((_alias, idx) => !action.payload.includes(idx));
			const newSet = new Set([...state.question.aliases, ...selectedCandidates]); // убираем повторные алиасы
			const uniqueAliases = Array.from(newSet);
			state.question.aliases = uniqueAliases;
		},
		// изменение статуса проверки вопроса
		editingAnswerChecked: (state, action: PayloadAction<{ idx: number; value: boolean }>) => {
			state.question.answers[action.payload.idx].checked = action.payload.value;
		},
		// изменение канала
		editingAnswerChannel: (state, action: PayloadAction<{ idx: number; channelId: string }>) => {
			state.question.answers[action.payload.idx].channel = action.payload.channelId;
		},
		// изменение вопроса в списке вопросов
		editingQuestionInQuestionsList: (state, action: PayloadAction<IQuestionItem>) => {
			state.questionsList.data = state.questionsList.data.map((questionItem) => {
				if (questionItem.id === action.payload.id)
					return { ...questionItem, question: action.payload.question, category: action.payload.category, incomplete: action.payload.incomplete };
				else return questionItem;
			});
		},
		// установка флага обновления списка вопросов
		setupUpdateQuestionsList: (state) => {
			state.questionsList.update = true;
		},
		// добавление каналов и количества ответов для каждого
		addChannelsIdAndCountAnswers: (state, action: PayloadAction<Record<string, number>>) => {
			state.question.channelsIdAndCountAnswers = action.payload;
		},
		// изменение текста ответа
		editingAnswerText: (state, action: PayloadAction<{ idx: number; value: string }>) => {
			state.question.answers[action.payload.idx].answer = action.payload.value;
		},
		// добавление ответа
		addAnswer: (state, action: PayloadAction<string>) => {
			state.question.answers.push({ checked: false, channel: action.payload, answer: "" });
		},
		// копирование ответа в канал
		copyAnswer: (state, action: PayloadAction<IAnswer>) => {
			state.question.answers.push(action.payload);
		},
		// перемещение ответа в канал
		moveAnswer: (state, action: PayloadAction<{ idx: number; channelId: string }>) => {
			state.question.answers[action.payload.idx].channel = action.payload.channelId;
		},
		// удаление ответа
		deleteAnswer: (state, action: PayloadAction<number>) => {
			state.question.answers.splice(action.payload, 1);
		},
		// уточнение удаления только ответов при удалении списка вопросов
		specifyDeletionOnlyAnswers: (state) => {
			state.deletingQuestion.deletingAnswers = true;
		},
		// установка флага только удаления кандидатов
		setupDeletionOnlyCandidates: (state) => {
			state.questionManipulation.approveCandidateList.deleteOnly = true;
		},
		// добавление флага автосохранения
		addFlagAutosaveQuestion: (state) => {
			state.editingQuestion.autosave = true;
		},
		// очистка state
		clearState: (state) => {
			state.questionsList = initialState.questionsList;
			state.question = initialState.question;
			state.editingQuestion = initialState.editingQuestion;
			state.deletingQuestion = initialState.deletingQuestion;
			state.replaceInQuestion = initialState.replaceInQuestion;
			state.editingAnswers = initialState.editingAnswers;

			state.questionManipulation = initialState.questionManipulation;
		},
		// очистка списка вопросов
		clearQuestionsList: (state) => {
			state.questionsList = initialState.questionsList;
		},
		// очистка ответов на вопрос
		clearQuestion: (state) => {
			state.question = initialState.question;
		},
		// очистка статуса добавления вопроса
		clearAddingQuestion: (state) => {
			state.addingQuestion = initialState.addingQuestion;
		},
		// очистка статуса изменения ответа на вопрос
		clearEditingQuestion: (state) => {
			state.editingQuestion = initialState.editingQuestion;
		},
		// очистка статуса удаления вопроса
		clearDeletingQuestion: (state) => {
			state.deletingQuestion = initialState.deletingQuestion;
		},
		// очистка данных замены текста в ответах
		clearReplaceInQuestion: (state) => {
			state.replaceInQuestion = initialState.replaceInQuestion;
		},
		// очистка данных замены ответов
		clearEditingAnswers: (state) => {
			state.editingAnswers = initialState.editingAnswers;
		},
		// очистка данных и статуса доп.манипуляций с вопросами
		clearQuestionManipulation: (state) => {
			state.questionManipulation = initialState.questionManipulation;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getQuestionsList.pending, (state) => {
				state.questionsList.status = RequestStatus.LOADING;
			})
			.addCase(getQuestionsList.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "questions" in action.payload) {
					state.questionsList.status = RequestStatus.IDLE;
					// если изменилось количество вопросов или нет флага обновления после запроса ask (поиск ответа)
					if (state.questionsList.data.length !== action.payload.questions.length || !state.questionsList.update) {
						state.questionsList.data = action.payload.questions.sort((a, b) => {
							if (a.question > b.question) return 1;
							else if (a.question < b.question) return -1;
							else return 0;
						});
					}
					state.questionsList.update = false;
				} else state.questionsList.status = RequestStatus.FAILED;
			})
			.addCase(getQuestionsList.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionsList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.questionsList.error = error;
				state.questionsList.message = message;
			})
			.addCase(getQuestion.pending, (state) => {
				state.question.status = RequestStatus.LOADING;
			})
			.addCase(getQuestion.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "answers" in action.payload) {
					state.question.status = RequestStatus.IDLE;
					// если нет ответов - замена с учетом случая не сохраненных ответов
					if (action.payload.answers.length === 0) state.question.answers = [{ answer: "", channel: "0", checked: false }];
					else state.question.answers = action.payload.answers;
					state.question.aliases = action.payload.aliases;
					state.question.candidates = action.payload.candidates;
				} else state.question.status = RequestStatus.FAILED;
			})
			.addCase(getQuestion.rejected, (state, action: PayloadAction<unknown>) => {
				state.question.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.question.error = error;
				state.question.message = message;
			})
			.addCase(addQuestion.pending, (state) => {
				state.addingQuestion.status = RequestStatus.LOADING;
			})
			.addCase(addQuestion.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingQuestion.status = RequestStatus.IDLE;
					state.addingQuestion.error = action.payload.error;
					state.addingQuestion.message = action.payload.message;
					if (action.payload.id) state.addingQuestion.id = action.payload.id;
				} else state.addingQuestion.status = RequestStatus.FAILED;
			})
			.addCase(addQuestion.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingQuestion.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingQuestion.error = error;
				state.addingQuestion.message = message;
			})
			.addCase(editQuestion.pending, (state) => {
				state.editingQuestion.status = RequestStatus.LOADING;
			})
			.addCase(editQuestion.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingQuestion.status = RequestStatus.IDLE;
					state.editingQuestion.error = action.payload.error;
					state.editingQuestion.message = action.payload.message;
				} else state.editingQuestion.status = RequestStatus.FAILED;
			})
			.addCase(editQuestion.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingQuestion.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingQuestion.error = error;
				state.editingQuestion.message = message;
			})
			.addCase(deleteQuestion.pending, (state) => {
				state.deletingQuestion.status = RequestStatus.LOADING;
			})
			.addCase(deleteQuestion.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingQuestion.status = RequestStatus.IDLE;
					state.deletingQuestion.error = action.payload.error;
					state.deletingQuestion.message = action.payload.message;
				} else state.deletingQuestion.status = RequestStatus.FAILED;
			})
			.addCase(deleteQuestion.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingQuestion.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingQuestion.error = error;
				state.deletingQuestion.message = message;
			})
			.addCase(replaceTextInAllAnswers.pending, (state) => {
				state.replaceInQuestion.status = RequestStatus.LOADING;
			})
			.addCase(replaceTextInAllAnswers.fulfilled, (state, action) => {
				if (action.payload) {
					state.replaceInQuestion.status = RequestStatus.IDLE;
					state.replaceInQuestion.error = action.payload.error;
					state.replaceInQuestion.message = action.payload.message;
					if ("replacements" in action.payload && typeof action.payload.replacements === "number") {
						state.replaceInQuestion.replacements = action.payload.replacements;
					}
				} else state.replaceInQuestion.status = RequestStatus.FAILED;
			})
			.addCase(replaceTextInAllAnswers.rejected, (state, action: PayloadAction<unknown>) => {
				state.replaceInQuestion.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.replaceInQuestion.error = error;
				state.replaceInQuestion.message = message;
			})
			.addCase(deleteQuestionsList.pending, (state) => {
				state.deletingQuestion.status = RequestStatus.LOADING;
			})
			.addCase(deleteQuestionsList.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingQuestion.status = RequestStatus.IDLE;
					state.deletingQuestion.error = action.payload.error;
					state.deletingQuestion.message = action.payload.message;
				} else state.deletingQuestion.status = RequestStatus.FAILED;
			})
			.addCase(deleteQuestionsList.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingQuestion.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingQuestion.error = error;
				state.deletingQuestion.message = message;
			})
			.addCase(modifyAnswers.pending, (state) => {
				state.editingAnswers.status = RequestStatus.LOADING;
			})
			.addCase(modifyAnswers.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingAnswers.status = RequestStatus.IDLE;
					state.editingAnswers.error = action.payload.error;
					state.editingAnswers.message = action.payload.message;
				} else state.editingAnswers.status = RequestStatus.FAILED;
			})
			.addCase(modifyAnswers.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingAnswers.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingAnswers.error = error;
				state.editingAnswers.message = message;
			})
			.addCase(getQuestionsListForMoving.pending, (state) => {
				state.questionManipulation.questionsList.status = RequestStatus.LOADING;
			})
			.addCase(getQuestionsListForMoving.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "questions" in action.payload) {
					state.questionManipulation.questionsList.status = RequestStatus.IDLE;
					state.questionManipulation.questionsList.data = action.payload.questions.sort((a, b) => {
						if (a.question > b.question) return 1;
						else if (a.question < b.question) return -1;
						else return 0;
					});
				} else state.questionsList.status = RequestStatus.FAILED;
			})
			.addCase(getQuestionsListForMoving.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionManipulation.questionsList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.questionManipulation.questionsList.error = error;
				state.questionManipulation.questionsList.message = message;
			})
			.addCase(getQuestionForMoving.pending, (state) => {
				state.questionManipulation.question.status = RequestStatus.LOADING;
			})
			.addCase(getQuestionForMoving.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "answers" in action.payload) {
					state.questionManipulation.question.status = RequestStatus.IDLE;
					state.questionManipulation.question.answers = action.payload.answers;
					state.questionManipulation.question.aliases = action.payload.aliases;
					state.questionManipulation.question.candidates = action.payload.candidates;
				} else state.questionManipulation.question.status = RequestStatus.FAILED;
			})
			.addCase(getQuestionForMoving.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionManipulation.question.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.questionManipulation.question.error = error;
				state.questionManipulation.question.message = message;
			})
			.addCase(editQuestionForMoving.pending, (state) => {
				state.questionManipulation.editingQuestion.status = RequestStatus.LOADING;
			})
			.addCase(editQuestionForMoving.fulfilled, (state, action) => {
				if (action.payload) {
					state.questionManipulation.editingQuestion.status = RequestStatus.IDLE;
					state.questionManipulation.editingQuestion.error = action.payload.error;
					state.questionManipulation.editingQuestion.message = action.payload.message;
				} else state.questionManipulation.editingQuestion.status = RequestStatus.FAILED;
			})
			.addCase(editQuestionForMoving.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionManipulation.editingQuestion.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.questionManipulation.editingQuestion.error = error;
				state.questionManipulation.editingQuestion.message = message;
			})
			.addCase(appendAliasList.pending, (state) => {
				state.questionManipulation.appendAliasList.status = RequestStatus.LOADING;
			})
			.addCase(appendAliasList.fulfilled, (state, action) => {
				if (action.payload) {
					state.questionManipulation.appendAliasList.status = RequestStatus.IDLE;
					state.questionManipulation.appendAliasList.error = action.payload.error;
					state.questionManipulation.appendAliasList.message = action.payload.message;
				} else state.questionManipulation.appendAliasList.status = RequestStatus.FAILED;
			})
			.addCase(appendAliasList.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionManipulation.appendAliasList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.questionManipulation.appendAliasList.error = error;
				state.questionManipulation.appendAliasList.message = message;
			})
			.addCase(approveCandidateList.pending, (state) => {
				state.questionManipulation.approveCandidateList.status = RequestStatus.LOADING;
			})
			.addCase(approveCandidateList.fulfilled, (state, action) => {
				if (action.payload) {
					state.questionManipulation.approveCandidateList.status = RequestStatus.IDLE;
					state.questionManipulation.approveCandidateList.error = action.payload.error;
					state.questionManipulation.approveCandidateList.message = action.payload.message;
				} else state.questionManipulation.approveCandidateList.status = RequestStatus.FAILED;
			})
			.addCase(approveCandidateList.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionManipulation.approveCandidateList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.questionManipulation.approveCandidateList.error = error;
				state.questionManipulation.approveCandidateList.message = message;
			});
	},
});

export const {
	addActiveQuestionId,
	editingAnswerAlias,
	addAlias,
	deleteAlias,
	deleteAliases,
	moveCandidatesToAliases,
	editingAnswerChecked,
	editingAnswerChannel,
	editingQuestionInQuestionsList,
	setupUpdateQuestionsList,
	addChannelsIdAndCountAnswers,
	editingAnswerText,
	addAnswer,
	copyAnswer,
	moveAnswer,
	deleteAnswer,
	specifyDeletionOnlyAnswers,
	setupDeletionOnlyCandidates,
	addFlagAutosaveQuestion,
	clearState,
	clearQuestionsList,
	clearQuestion,
	clearAddingQuestion,
	clearEditingQuestion,
	clearDeletingQuestion,
	clearReplaceInQuestion,
	clearEditingAnswers,
	clearQuestionManipulation,
} = qasQuestionSlice.actions;

export const selectQuestionsList = (state: RootState) => state.qasQuestion.questionsList;
export const selectQuestion = (state: RootState) => state.qasQuestion.question;
export const selectAddingQuestion = (state: RootState) => state.qasQuestion.addingQuestion;
export const selectEditingQuestion = (state: RootState) => state.qasQuestion.editingQuestion;
export const selectDeletingQuestion = (state: RootState) => state.qasQuestion.deletingQuestion;
export const selectReplaceInQuestion = (state: RootState) => state.qasQuestion.replaceInQuestion;
export const selectEditingAnswers = (state: RootState) => state.qasQuestion.editingAnswers;

export const selectQuestionsListForMoving = (state: RootState) => state.qasQuestion.questionManipulation.questionsList;
export const selectQuestionsForMoving = (state: RootState) => state.qasQuestion.questionManipulation.question;
export const selectEditStatusForMoving = (state: RootState) => state.qasQuestion.questionManipulation.editingQuestion;
export const selectAppendAliasList = (state: RootState) => state.qasQuestion.questionManipulation.appendAliasList;
export const selectApproveCandidateList = (state: RootState) => state.qasQuestion.questionManipulation.approveCandidateList;

export default qasQuestionSlice.reducer;
